import {
  getSpaPackageConfigGroups,
  getSpaPackageConfigGroupsKey,
} from '@features/spa/hooks/useGetSpaPackageConfigGroups';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { canGuestsShareTimeslot, getOptionTimeslots } from '../helpers/timeslotCapacity';

const CONFIG_GROUPS_CACHE_TIME = 60000;

interface UseMultiGuestTimeslotAvailabilityOptions {
  dates: (string | undefined)[];
  numberOfGuests: number;
  propertyId?: number;
  spaPackageId?: number;
  spaOpenTime?: string;
  spaCloseTime?: string;
}

interface UseMultiGuestTimeslotAvailabilityReturn {
  canGuestsShareTimeslotOn: (date: string, optionIds: number[]) => boolean | undefined;
  hasTimeslotsOn: (date: string, optionIds: number[]) => boolean;
}

export const useMultiGuestTimeslotAvailability = ({
  dates,
  numberOfGuests,
  propertyId,
  spaPackageId,
  spaOpenTime,
  spaCloseTime,
}: UseMultiGuestTimeslotAvailabilityOptions): UseMultiGuestTimeslotAvailabilityReturn => {
  const isEnabled = numberOfGuests > 1 && !!propertyId && !!spaPackageId && !!spaOpenTime;

  const datesKey = (Array.isArray(dates) ? dates : []).filter(Boolean).join(',');

  const uniqueDates = useMemo(
    () => (isEnabled && datesKey ? Array.from(new Set(datesKey.split(','))) : []),
    [datesKey, isEnabled]
  );

  const configGroupsQuery = (date: string, quantity: number) => ({
    queryKey: getSpaPackageConfigGroupsKey(
      Number(propertyId),
      Number(spaPackageId),
      date,
      quantity,
      spaOpenTime,
      spaCloseTime
    ),
    queryFn: () =>
      getSpaPackageConfigGroups(
        Number(propertyId),
        Number(spaPackageId),
        date,
        quantity,
        spaOpenTime,
        spaCloseTime
      ),
    staleTime: CONFIG_GROUPS_CACHE_TIME,
    cacheTime: CONFIG_GROUPS_CACHE_TIME,
    retry: false,
  });

  const partyResults = useQueries({
    queries: uniqueDates.map((date) => configGroupsQuery(date, numberOfGuests)),
  });

  const singleGuestResults = useQueries({
    queries: uniqueDates.map((date) => configGroupsQuery(date, 1)),
  });

  const configGroupsOn = (
    results: typeof partyResults,
    date: string
  ): ConfigurationGroupType[] | undefined => {
    const dateIndex = uniqueDates.indexOf(date);
    const result = dateIndex === -1 ? undefined : results[dateIndex];

    return result?.data as ConfigurationGroupType[] | undefined;
  };

  const bookableOptionIds = (optionIds: number[]) =>
    (Array.isArray(optionIds) ? optionIds : []).filter(
      (optionId) => !!optionId && !Number.isNaN(optionId)
    );

  const canGuestsShareTimeslotOn = (date: string, optionIds: number[]): boolean | undefined => {
    if (numberOfGuests < 2) {
      return false;
    }

    const configurationGroups = configGroupsOn(partyResults, date);

    if (!configurationGroups) {
      return undefined;
    }

    return bookableOptionIds(optionIds).some((optionId) =>
      canGuestsShareTimeslot(getOptionTimeslots(configurationGroups, optionId), numberOfGuests)
    );
  };

  const hasTimeslotsOn = (date: string, optionIds: number[]): boolean => {
    const configurationGroups = configGroupsOn(singleGuestResults, date);

    if (!configurationGroups) {
      return false;
    }

    return bookableOptionIds(optionIds).some(
      (optionId) => getOptionTimeslots(configurationGroups, optionId).length > 0
    );
  };

  return { canGuestsShareTimeslotOn, hasTimeslotsOn };
};
